import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { Button, Col, Collapse, Form, Row, Typography, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import ConsultantForm from "../CommonComponents/ConsultantForm";
import PatientProfile from "../CommonComponents/PatientProfile";
import ClinicalImpression from "../CommonComponents/ClinicalImpression";
import HistoryIllness from "../CommonComponents/HistoryIllness";
import Medication from "../CommonComponents/Medication";
import EmergencyVitals from "./EmergencyVitals";
import EmergencyCareItems, { isExcludedCareType } from "./EmergencyCareItems";
import EmergencyInvestigations from "./EmergencyInvestigations";
import EmergencyReasonForTransfer from "./EmergencyReasonForTransfer";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import {
  getPatientDetailsApi,
  getListEmergencyShiftFormApi,
  getEmergencyShiftFormByIdApi,
  updateEmergencyShiftFormByIdApi,
  updateEmergencyShiftFormHtmlByIdApi,
  printFormApi,
  shareIpFormApi,
  submitEmergencyShiftFormApi,
} from "../../../../services/forms.services";
import {
  knownAllergiesOptions,
  pastMedicalOptions,
} from "../../../IpEMR/Options/historyOptions";
import {
  getLabelTagsWithFormDescription,
  getPastMedicalHistoryFormSummary,
  printFormSummary,
} from "../formsKeys";
import useQuery from "../../../../hooks/useQuery";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import assessmentIcon from "../../../../assets/images/nurseIcons/assessment.svg";
import FormContentLoader from "../FormContentLoader";
import FormDocumentView from "../CommonComponents/FormDocumentView";
import ShareModal from "../../Forms/DischargeSummary/ShareModal";
import ReviewModal from "../ReviewForms/ReviewModal";
import {
  isHtmlDocument,
  isWithinEditWindow,
  normalizeRecordList,
} from "../CommonComponents/formRecords";
import "../ReviewForms.css";
import "../FormsIp.css";
import "./EmergencyShiftForm.css";

const FORM_TYPE = "emergency_shift";
const FORM_TITLE = "Emergency Shift Form";

const EmergencyShiftForm = ({ patientData, role }) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const commonStyle = {
    boxShadow:
      "0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
  };

  const [formRecord, setFormRecord] = useState(null);
  const [editable, setEditable] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [details, setDetails] = useState(null);
  const [displayForm, setDisplayForm] = useState(null);
  // Set on a successful submit so the saved document opens in the editor
  // instead of the read-only preview.
  const [openEditorAfterSubmit, setOpenEditorAfterSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [openShare, setOpenShare] = useState(false);
  const [sharePending, setSharePending] = useState(null);
  const [recordList, setRecordList] = useState([]);
  const [openHistory, setOpenHistory] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);

  const refreshPage = () => setRefresh((prev) => !prev);
  const isActive = useQuery()?.status === "current";
  // The form records a doctor's decision to move the patient out, so only the
  // doctor's EMR offers it; everyone else sees the submitted document at most.
  const isDoctor = role === "doctor";
  // The receptionist reads the raised form to print and share it; the
  // document editor and New Form stay with the doctor who raised it.
  const isReceptionist = role === "receptionist";
  const canEditForm = isDoctor && editable;
  const careItems = details?.data?.care_items;

  const loadRecordDisplay = async (record, isCancelled = () => false) => {
    if (!record?.id) {
      if (!isCancelled()) setDisplayForm(null);
      return;
    }
    if (isHtmlDocument(record?.pdf_html)) {
      if (!isCancelled()) setDisplayForm(record.pdf_html);
      return;
    }
    try {
      const savedRes = await getEmergencyShiftFormByIdApi(record.id);
      const saved = savedRes?.data?.data?.pdf_html ?? savedRes?.data?.pdf_html;
      if (isHtmlDocument(saved)) {
        if (!isCancelled()) setDisplayForm(saved);
        return;
      }
    } catch (err) {
      console.log(err);
    }
    try {
      const previewRes = await printFormApi(
        `form_type=${FORM_TYPE}&form_id=${record.id}`
      );
      if (!isCancelled()) setDisplayForm(previewRes?.data?.data ?? null);
    } catch (err) {
      console.log(err);
      if (!isCancelled()) setDisplayForm(null);
    }
  };

  useEffect(() => {
    if (!patientData?.admission?.id || !patientData?.doctor_intial_assessment) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;
    setIsLoading(true);
    setDisplayForm(null);

    const loadForm = async () => {
      try {
        const [listRes, detailsRes] = await Promise.all([
          getListEmergencyShiftFormApi(patientData.admission.id).catch((err) => {
            if (err?.response?.status === 404) return null;
            throw err;
          }),
          getPatientDetailsApi(patientData.admission.id),
        ]);
        if (cancelled) return;

        const list = normalizeRecordList(listRes?.data);
        setRecordList(list);
        setDetails(detailsRes?.data);

        if (isNewForm) {
          setFormRecord(null);
          setEditable(isActive && isDoctor);
          return;
        }

        // Keep whichever record is open selected across a refresh, so saving
        // an edit to an older entry does not jump to the newest one.
        const openId = formRecord?.id;
        const record =
          list.find((row) => row?.id === openId) ??
          list[list.length - 1] ??
          null;

        setFormRecord(record);
        if (!isDoctor || !isActive) {
          setEditable(false);
        } else {
          setEditable(record?.id ? false : true);
        }

        await loadRecordDisplay(record, isCancelled);
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadForm();
    return () => {
      cancelled = true;
    };
  }, [
    patientData?.admission?.id,
    patientData?.doctor_intial_assessment,
    refresh,
    isDoctor,
    isActive,
  ]);

  // Restore a saved record into the form controls.
  useEffect(() => {
    if (!formRecord?.data) return;
    form.setFieldsValue({ ...withGrbsSplit(formRecord.data) });
  }, [formRecord, form]);

  const handleNewForm = () => {
    setOpenEditorAfterSubmit(false);
    setIsNewForm(true);
    setFormRecord(null);
    form.resetFields();
    setDisplayForm(null);
    setEditable(true);
    /* Care items, vitals and medication on a new form come from the
       patient details loaded with the page. A doctor who ordered
       something in another tab would otherwise fill this form against
       what was on screen when it opened, so it is re-read here. */
    refreshPage();
  };

  /*
   * Back to the saved form, from a New Form or an Edit.
   *
   * Opening either replaces the document on screen with the editor, and
   * nothing put it back - the only way out was to leave the patient. An edit
   * returns to the record it was opened on; a new form returns to the most
   * recent one, which is what was on screen before New Form was pressed.
   * Offered only when there is a saved record to go back to.
   */
  const latestRecord = recordList?.[recordList.length - 1] || null;
  const recordToReturnTo = formRecord?.id ? formRecord : latestRecord;

  const backToSavedForm = () => {
    if (!recordToReturnTo?.id) return;
    handleHistoryAction(recordToReturnTo, false);
  };

  const handleHistoryAction = (record, isEdit) => {
    setOpenEditorAfterSubmit(false);
    form.resetFields();
    setIsNewForm(false);
    setOpenHistory(false);
    setFormRecord(record || null);
    setEditable(isEdit && isDoctor && isActive);
    setDisplayForm(null);
    setIsLoading(true);
    loadRecordDisplay(record).finally(() => setIsLoading(false));
  };

  /*
   * GRBS and insulin used to be stored inside the vitals reading. They are
   * their own chart and are now submitted under their own key, so a form
   * saved before that moves them across when it is reopened - otherwise the
   * two boxes come up empty on an older record.
   */
  const withGrbsSplit = (data) => {
    if (!data || typeof data !== "object") return data;
    const { grbs, insulin, ...vitals } = data.vitals || {};
    if (grbs === undefined && insulin === undefined) return data;
    return {
      ...data,
      vitals,
      grbs: { grbs, insulin, ...(data.grbs || {}) },
    };
  };

  const onFinish = (values) => {
    const presentIllness = details?.data?.present_illness;
    const formData = {
      patient: details?.data?.patient,
      diagnosis: details?.data?.diagnosis,
      medications: details?.data?.medications,
      nebulization: details?.data?.nebulization,
      // The care items that are shown on this form are the ones stored with it,
      // so the printed document matches what the doctor reviewed.
      care_items: Array.isArray(careItems)
        ? careItems.filter(
            /* The same test the panel above uses - this compared the display
               name against a list of codes, so it never matched and the saved
               document carried items the screen had left out. */
            (item) => !isExcludedCareType(item)
          )
        : [],
      past_medical_history: presentIllness
        ? getPastMedicalHistoryFormSummary(pastMedicalOptions, presentIllness)
        : "",
      known_allergies: presentIllness
        ? getLabelTagsWithFormDescription(knownAllergiesOptions, presentIllness)
        : "",
      past_surgical_history: presentIllness?.surgical_history ?? null,
      past_surgical_history_details:
        presentIllness?.surgical_history_details ?? null,
      ...values,
    };

    const formId = formRecord?.id;
    if (!formId && !isNewForm && recordList?.length) {
      message.error(
        "Could not tell which form is open. Please reopen it from History and try again."
      );
      return;
    }
    if (formId) {
      updateEmergencyShiftFormByIdApi(formId, { data: formData })
        .then((res) => {
          message.success(res?.data?.message || `${FORM_TITLE} updated.`);
          setEditable(false);
          // Drop the previous document so the editor cannot open on stale
          // html while the saved version is being refetched.
          setDisplayForm(null);
          setIsLoading(true);
          setOpenEditorAfterSubmit(true);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    } else {
      submitEmergencyShiftFormApi({
        ip_admission: patientData?.admission?.id,
        data: formData,
      })
        .then((res) => {
          message.success(res?.data?.message || `${FORM_TITLE} submitted.`);
          setIsNewForm(false);
          setEditable(false);
          setDisplayForm(null);
          setIsLoading(true);
          setOpenEditorAfterSubmit(true);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    }
  };

  // TinyMCE edits live on pdf_html rather than the structured data, so the
  // printed document keeps the manual wording.
  const saveDocumentHtml = async (html, { refresh = true } = {}) => {
    const formId = formRecord?.id;
    if (!formId) return false;
    try {
      const res = await updateEmergencyShiftFormHtmlByIdApi(formId, html);
      message.success(res?.data?.message || `${FORM_TITLE} saved.`);
      if (refresh) refreshPage();
      return true;
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to save. Please try again."
      );
      return false;
    }
  };

  /* Raising the form is the doctor's call, but the receptionist is the one
     who hands it over - they read the raised form to print and share it, the
     same as their review form. Every other role is kept out. */
  if (!isDoctor && !isReceptionist) {
    return (
      <NoDataPage
        title={`${FORM_TITLE} Not Available`}
        subtitle="Only a doctor can raise an emergency shift form"
        image={assessmentIcon}
      />
    );
  }

  if (!patientData?.doctor_intial_assessment) {
    return (
      <Row
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
        }}
      >
        <Col span={24} style={{ textAlign: "center" }}>
          <NoDataPage
            title="Initial Assessment Not Yet Submitted "
            subtitle="Kindly, submit initial assessment first"
            image={assessmentIcon}
          />
        </Col>
      </Row>
    );
  }

  return (
    <FormContentLoader loading={isLoading} tip="Loading emergency shift form...">
      <div style={{ position: "relative" }}>
        {canEditForm && (
          <div className="forms-ip-header">
            <Typography.Title level={5} className="forms-ip-header-title">
              {FORM_TITLE}
            </Typography.Title>
            <div className="forms-ip-header-actions">
              {formRecord?.id && (
                <Button
                  type="primary"
                  onClick={() =>
                    printFormSummary(FORM_TYPE, formRecord?.id, displayForm)
                  }
                >
                  Print
                </Button>
              )}
              {/* Closes the editor and puts the saved form back on screen -
                  the same mark Referral, Investigation Referral and Death
                  Summary already close their editors with. */}
              {recordToReturnTo?.id && (
                <CloseOutlined
                  onClick={backToSavedForm}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      backToSavedForm();
                    }
                  }}
                  title="Close without saving and go back to the saved form"
                  aria-label="Close without saving and go back to the saved form"
                />
              )}
            </div>
          </div>
        )}
        {canEditForm ? (
          <Form
            form={form}
            ref={formRef}
            layout="vertical"
            onFinish={onFinish}
            className="forms-ip-shell forms-ip-scroll-shell"
            initialValues={{ ...withGrbsSplit(formRecord?.data) }}
          >
            <Collapse
              expandIconPosition="end"
              className="custom-collapse forms-ip-collapse"
              defaultActiveKey={["1", "2", "3", "4", "5", "6", "7", "8", "9"]}
            >
              <Collapse.Panel
                header="Patient Profile"
                style={commonStyle}
                key="1"
              >
                <PatientProfile data={patientData?.admission} />
              </Collapse.Panel>
              <Collapse.Panel header="Consultants" style={commonStyle} key="2">
                <ConsultantForm form={form} />
              </Collapse.Panel>
              <Collapse.Panel
                header="Clinical Impression/ Diagnosis"
                style={commonStyle}
                key="3"
              >
                <ClinicalImpression details={details?.data?.diagnosis} />
              </Collapse.Panel>
              <Collapse.Panel
                header="History of Present Illness"
                style={commonStyle}
                key="4"
              >
                <HistoryIllness
                  form={form}
                  patientData={patientData?.admission}
                  data={details?.data?.present_illness}
                  details={details?.data}
                />
              </Collapse.Panel>
              <Collapse.Panel header="Vitals" style={commonStyle} key="5">
                <EmergencyVitals form={form} details={details} />
              </Collapse.Panel>
              <Collapse.Panel header="Medication" style={commonStyle} key="6">
                <Medication details={details} />
              </Collapse.Panel>
              <Collapse.Panel header="Care Items" style={commonStyle} key="7">
                <EmergencyCareItems careItems={careItems} />
              </Collapse.Panel>
              <Collapse.Panel
                header="Recent Lab Needs"
                style={commonStyle}
                key="8"
              >
                <EmergencyInvestigations />
              </Collapse.Panel>
              <Collapse.Panel
                header="Reason for Transfer / Shift"
                style={{ ...commonStyle, marginBottom: "40px" }}
                key="9"
              >
                <EmergencyReasonForTransfer />
              </Collapse.Panel>
            </Collapse>
            <div
              className="ip-fixed-form-footer"
              style={{
                position: "fixed",
                bottom: 0,
                left: 0,
                width: "100%",
                background: "#fff",
                paddingTop: "10px",
                paddingRight: "16px",
                boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.1)",
                textAlign: "right",
                zIndex: 1000,
              }}
            >
              {isActive && (
                <Form.Item>
                  <Button
                    htmlType="submit"
                    type="primary"
                    className={btnStyles.lgBtn}
                  >
                    {formRecord?.id ? "Update" : "Submit"}
                  </Button>
                </Form.Item>
              )}
            </div>
          </Form>
        ) : displayForm ? (
          <FormDocumentView
            html={displayForm}
            canEdit={
              isActive &&
              !isReceptionist &&
              isWithinEditWindow(formRecord?.created_at)
            }
            autoEdit={openEditorAfterSubmit}
            onAutoEditStart={() => setOpenEditorAfterSubmit(false)}
            onSave={saveDocumentHtml}
            onShare={(pendingHtml, onSaved) => {
              setSharePending(pendingHtml ? { pendingHtml, onSaved } : null);
              setOpenShare(true);
            }}
            leading={
              <div className="review-form-doc-leading">
                <span className="review-form-title-text">{FORM_TITLE}</span>
                {/* Several shift forms can be raised for one admission, so the
                    document on screen says which one it is - otherwise an
                    entry opened from History is indistinguishable from the
                    latest. */}
                {formRecord?.created_at && (
                  <span className="review-form-doc-date">
                    {dayjs(formRecord.created_at).format(
                      "DD MMM YYYY, hh:mm A"
                    )}
                  </span>
                )}
                {!editable && isActive && !isReceptionist && (
                  <Button
                    className="ds-btn ds-btn--ghost"
                    onClick={handleNewForm}
                  >
                    New Form
                  </Button>
                )}
              </div>
            }
            extraActions={
              <>
                {recordList?.length > 0 && (
                  <Button
                    className="ds-btn ds-btn--ghost"
                    onClick={() => setOpenHistory(true)}
                  >
                    History
                  </Button>
                )}
                <Button
                  className="ds-btn ds-btn--ghost"
                  onClick={() =>
                    printFormSummary(FORM_TYPE, formRecord?.id, displayForm)
                  }
                >
                  Print
                </Button>
              </>
            }
          />
        ) : (
          <NoDataPage
            title={`${FORM_TITLE} Not Available`}
            subtitle={`${FORM_TITLE} has not been submitted yet`}
            image={assessmentIcon}
          />
        )}
        {openHistory && (
          <ReviewModal
            open={openHistory}
            setOpen={setOpenHistory}
            reviewList={recordList}
            isActive={isActive}
            title={`${FORM_TITLE} History`}
            onShowClick={(record) => handleHistoryAction(record, false)}
          />
        )}
        {openShare && (
          <ShareModal
            openModal={openShare}
            setOpenModal={(open) => {
              setOpenShare(open);
              if (!open) {
              /* The save was held back from refreshing so the share could
                 address the record it had just written; refresh now that the
                 modal is done with it. */
              if (sharePending) refreshPage();
              setSharePending(null);
            }
            }}
            id={formRecord?.id}
            onBeforeSend={
              sharePending
                ? async () => {
                    const ok = await saveDocumentHtml(sharePending.pendingHtml, {
                        refresh: false,
                      });
                    if (ok) sharePending.onSaved?.();
                    return ok;
                  }
                : undefined
            }
            patientData={patientData}
            title={`Share ${FORM_TITLE}`}
            documentLabel={FORM_TITLE}
            shareApi={(payload) =>
              shareIpFormApi(FORM_TYPE, formRecord?.id, payload)
            }
          />
        )}
      </div>
    </FormContentLoader>
  );
};

export default EmergencyShiftForm;
